// components/features/users/UserRow.tsx

import { Admin, User } from "../../../types/features/User";
import { Agent } from "../../../types/features/Agent";
import { Entreprise } from "../../../types/features/Entreprise";
import TableRow from "../../ui/TableRow";

interface UserRowProps {
  user: User;
  onAction: (user: User) => void;
  onSelect?: (user: User, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function UserRow({
  user,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: UserRowProps) {
  const fullName = `${user.first_name} ${user.last_name}`.trim() || "Nouvel utilisateur";
  
  // Rôle avec couleur
  const getRoleBadge = (role: string) => {
    const roles = {
      admin: "bg-purple-100 text-purple-800",
      entreprise: "bg-blue-100 text-blue-800",
      agent: "bg-green-100 text-green-800"
    };
    return roles[role as keyof typeof roles] || "bg-gray-100 text-gray-800";
  };

  // Statut avec couleur
  const getStatusBadge = (status: string) => {
    const statuses = {
      ACTIVE: "bg-green-100 text-green-800",
      INACTIVE: "bg-gray-100 text-gray-800"
    };
    return statuses[status as keyof typeof statuses] || "bg-gray-100 text-gray-800";
  };

  // Vérification avec couleur
  const getVerifiedBadge = (verified: string) => {
    return verified === "YES" 
      ? "bg-green-100 text-green-800" 
      : "bg-yellow-100 text-yellow-800";
  };

  // Initiales
  const initials = `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase() || "?";

  // Obtenir le nom de l'entreprise si le rôle est entreprise
  const getProfileName = (user: User) => {
    if (user.role === "entreprise" && user.profile) {
      const profile = user.profile as Entreprise;
      return profile.name || "Entreprise";
    }
    if (user.role === "admin" && user.profile) {
      const profile = user.profile as Admin;
      return profile.is_system_admin ? "Super Admin" : "Admin";
    }
    if (user.role === "agent" && user.profile) {
      const profile = user.profile as Agent;
      return profile.full_name || "Agent";
    }
    return "";
  };

  const profileName = getProfileName(user);

  return (
    <TableRow
      item={user}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Utilisateur : Avatar + Nom + Email */}
      <td className="py-3 px-4">
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          {user.avatar ? (
            <img 
              src={user.avatar} 
              alt={fullName} 
              className="h-9 w-9 rounded-full object-cover shrink-0 border border-gray-200"
            />
          ) : (
            <div className={`h-9 w-9 rounded-full flex items-center justify-center font-semibold text-xs shrink-0 border ${
              user.role === "admin" 
                ? "bg-purple-100 text-purple-700 border-purple-200"
                : user.role === "entreprise"
                ? "bg-blue-100 text-blue-700 border-blue-200"
                : "bg-green-100 text-green-700 border-green-200"
            }`}>
              {initials}
            </div>
          )}

          {/* Informations */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900 block truncate">
                {fullName}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getRoleBadge(user.role)}`}>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500 block truncate max-w-[150px] sm:max-w-[200px]">
                {user.email}
              </span>
              {profileName && (
                <span className="text-xs text-gray-400">
                  • {profileName}
                </span>
              )}
            </div>
          </div>
        </div>
      </td>

      {/* Colonne Téléphone */}
      <td className="hidden sm:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-700 text-sm">
          {user.phonenumber || "—"}
        </span>
      </td>

      {/* Colonne Statut */}
      <td className="hidden md:table-cell py-3 px-4 whitespace-nowrap">
        <div className="flex flex-col space-y-0.5">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadge(user.status)}`}>
            {user.status}
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${getVerifiedBadge(user.is_verified)}`}>
            {user.is_verified === "YES" ? "Vérifié" : "Non vérifié"}
          </span>
        </div>
      </td>

      {/* Colonne Type de connexion */}
      <td className="hidden lg:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-500 text-xs">
          {user.connexion_type || "—"}
        </span>
      </td>

      {/* Colonne Date de création */}
      <td className="hidden xl:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-gray-500 text-xs">
          {user.created_at
            ? new Date(user.created_at).toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              })
            : "—"}
        </span>
      </td>
    </TableRow>
  );
}